import Grid from '@material-ui/core/Grid';
import {
  HomePageStarredEntities,
  HomePageToolkit,
  WelcomeTitle,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { Content, Header, InfoCard, Page } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import Typography from '@material-ui/core/Typography';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CloudQueue from '@material-ui/icons/CloudQueue';
import Timeline from '@material-ui/icons/Timeline';
import Code from '@material-ui/icons/Code';
import { MyOwnedEntitiesCard } from './MyOwnedEntitiesCard';

type Tool = { label: string; url: string; icon: JSX.Element };

/**
 * Builds the "Platform tools" links from config rather than literals.
 *
 * ArgoCD and Grafana are per-environment, and one image serves both the dev
 * and the prod cluster, so their hostnames cannot live in the bundle. They
 * come from app.platformTools.*, which app-config.production.yaml derives
 * from `${DOMAIN}` — see packages/app/config.d.ts.
 *
 * A tool whose URL is unset is omitted, which is how Grafana and Azure DevOps
 * are currently disabled: their keys are commented out in the config files,
 * not deleted from here. Docs is always present because it is a route in this
 * app, not an external site.
 */
const PlatformToolsCard = () => {
  const config = useApi(configApiRef);
  const url = (key: string) =>
    config.getOptionalString(`app.platformTools.${key}`);

  const argocdUrl = url('argocdUrl');
  const grafanaUrl = url('grafanaUrl');
  const azureDevOpsUrl = url('azureDevOpsUrl');

  const tools: Tool[] = [
    ...(argocdUrl
      ? [{ label: 'ArgoCD', url: argocdUrl, icon: <CloudQueue /> }]
      : []),
    ...(grafanaUrl
      ? [{ label: 'Grafana', url: grafanaUrl, icon: <Timeline /> }]
      : []),
    ...(azureDevOpsUrl
      ? [{ label: 'Azure DevOps', url: azureDevOpsUrl, icon: <Code /> }]
      : []),
    { label: 'Docs', url: '/docs', icon: <LibraryBooks /> },
  ];

  return <HomePageToolkit title="Platform tools" tools={tools} />;
};

/**
 * DCP landing page.
 *
 * Orientation first: what the platform is, how to get access, where the
 * docs are, and what you already own. Live platform status is
 * intentionally absent until the portal has users — see the design spec.
 */
export const HomePage = () => (
  <Page themeId="home">
    {/*
      WelcomeTitle picks a random greeting from 62 languages on every render,
      which reads as a bug rather than a flourish on a platform portal. Pin it
      to English; the component keeps its time-of-day awareness.
    */}
    <Header
      title={<WelcomeTitle language={['English']} />}
      pageTitleOverride="Home"
    />
    <Content>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HomePageSearchBar placeholder="Search services, docs and templates" />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard title="Dräger Compute Platform">
            <Typography paragraph>
              DCP runs managed Kubernetes for Dräger product teams. Each
              team is onboarded as a tenant with its own namespaces, an
              ArgoCD project, and access governed by Entra groups.
            </Typography>
            <Typography paragraph>
              To get started, request a namespace from{' '}
              <strong>Create</strong> in the sidebar. Platform reviews the
              request, and provisioning runs automatically once approved.
            </Typography>
            <Typography paragraph>
              Per-service documentation lives under <strong>Docs</strong>,
              alongside the service it describes.
            </Typography>
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <PlatformToolsCard />
        </Grid>

        <Grid item xs={12} md={6}>
          <MyOwnedEntitiesCard />
        </Grid>

        <Grid item xs={12} md={6}>
          <HomePageStarredEntities />
        </Grid>
      </Grid>
    </Content>
  </Page>
);
